/* eslint-disable @typescript-eslint/no-namespace */
import { DataType } from '@mode-switch/express';
import { LegendPosition } from './viz-format.types';

/**
 * The formatting groups that format shortcuts fall into.
 *
 * Each of these will have a formatter that handles transforming the type of expected value.
 * Each FormatShortcutType will essentially be a specific default configuration setting
 * for one of the formatters.
 */
export const enum FormatType {
  Default = 'default',
  Number = 'number',
  Percent = 'percent',
  Date = 'date',
  Financial = 'financial',
  Url = 'url',
  DatePart = 'datePart',
}

/**
 * Format shortcuts.
 *
 * Each format shortcut will be a specific default configuration for presentation.
 * This can include a formatter definition and/or a renderer definition.
 */
export enum FormatShortcutType {
  Automatic = 'automatic',
  PlainText = 'plainText',
  ShortNumber = 'shortNumber',
  LongNumber = 'longNumber',
  Percent = 'percent',
  MonthYear = 'monthYear', // Aug 2021
  MonthDay = 'monthDay', // 08/10
  Year = 'year', // 2021
  YearMonthDate = 'yearMonthDate', // 2021-08-10
  DateAndTime = 'dateAndTime', // 2021-08-10 16:15:31
  TimeISO = 'timeISO',
  Currency = 'currency',
  Accounting = 'accounting',
  Url = 'url',
  Custom = 'custom',
  // DatePart formats
  FullName = 'fullName',
  ShortName = 'shortName',
  Ordinal = 'ordinal',
  Numeric = 'numeric',
}

export type NegativeBehavior = 'parens' | 'minusSign' | undefined;
export type LinkStyle = 'underline' | 'none' | 'link-icon';
export type LinkColor = 'blue' | 'purple' | 'green' | 'orange' | 'pink' | 'black';

export enum HorizontalAlignment {
  Left = 'left',
  Center = 'center',
  Right = 'right',
}

export type DigitSeparator = ',' | '.' | ' ' | '';
export type DigitGrouping = 2 | 3;
export type DecimalPrecision = number;
export type DisplayUnit = 'K' | 'M' | 'B' | 'G' | 'T' | 'Q' | '';
// TODO: Move this and all field format stuff to some other type file
export const enum CurrencyPreset {
  USD = 'usd',
  EUR = 'eur',
  JPY = 'jpy',
  GBP = 'gbp',
  AUD = 'aud',
  CHF = 'chf',
  CAD = 'cad',
  MXN = 'mxn',
  CNY = 'cny',
  NZD = 'nzd',
  Custom = 'custom',
}

export const enum FieldFormatTypes {
  Axis = 'axisFormat',
  Header = 'headerFormat',
  Legend = 'legendFormat',
  Pane = 'paneFormat',
  Base = 'baseFormat',
}

export const enum FieldAxisTypes {
  Default = 'default',
  Title = 'title',
  Label = 'label',
  Scale = 'scale',
  Range = 'range',
  Interpolation = 'interpolation',
  BinnedAxisLabel = 'binnedAxisLabel',
}

export const enum FieldHeaderTypes {
  Default = 'default',
  Totals = 'totals',
  GrandTotals = 'grandTotals',
  Title = 'title',
  Label = 'label',
  ShowValues = 'showValues',
}

export const enum FieldLegendTypes {
  Title = 'title',
  ShowTitle = 'showTitle',
  Position = 'position',
  Show = 'show',
  SplitLegend = 'splitLegend',
}

export const enum FieldPaneTypes {
  Default = 'default',
  Label = 'label',
}

export const enum AxisScaleTypes {
  Linear = 'linear',
  Log = 'log',
}

export const enum BinnedAxisLabelTypes {
  Auto = 'Auto',
  Continuous = 'Continuous range',
  First = 'First value only',
}

export const enum HeaderAxisLocationTypes {
  Auto = 'auto',
  Axis = 'axis',
  Header = 'header',
}

export const enum FieldBaseTypes {
  DisplayName = 'displayName',
}

// Container for all the settings that can be applied to a field
export interface FieldFormatMap {
  [formula: string]: FieldFormat;
}

export interface FieldFormat {
  [FieldFormatTypes.Base]?: BaseFieldFormat;
  [FieldFormatTypes.Axis]: AxisFieldFormat;
  [FieldFormatTypes.Header]: HeaderFieldFormat;
  [FieldFormatTypes.Legend]: LegendFieldFormat;
  [FieldFormatTypes.Pane]: PaneFieldFormat;
}

export interface BaseFieldFormat {
  [FieldBaseTypes.DisplayName]?: string;
}

export interface AxisFieldFormat {
  [FieldAxisTypes.Title]?: TitleFormat;
  [FieldAxisTypes.Scale]?: ScaleFormat;
  [FieldAxisTypes.Label]?: LabelFormat;
  [FieldAxisTypes.BinnedAxisLabel]?: BinnedAxisLabelTypeFormat;
}

export interface ScaleFormat {
  [FieldAxisTypes.Default]?: TextFormat;
  [FieldAxisTypes.Range]?: RangeFormat;
  [FieldAxisTypes.Interpolation]?: AxisScaleTypeFormat;
}

export interface RangeFormat {
  isIndependent: boolean;
  min?: number;
  max?: number;
}

export interface LinearScaleTypeFormat {
  type: AxisScaleTypes.Linear;
}

export interface LogScaleTypeFormat {
  type: AxisScaleTypes.Log;
  base?: number;
}

export type AxisScaleTypeFormat = LinearScaleTypeFormat | LogScaleTypeFormat;

export interface BinSizeValueTypes {
  rawValue: number;
  formattedValue: string;
}

export interface AutoAxisLabelsTypeFormat {
  type: BinnedAxisLabelTypes.Auto;
}

export interface ContinuousAxisLabelsTypeFormat {
  type: BinnedAxisLabelTypes.Continuous;
}

export interface FirstAxisLabelsTypeFormat {
  type: BinnedAxisLabelTypes.First;
}

export type BinnedAxisLabelTypeFormat =
  | AutoAxisLabelsTypeFormat
  | ContinuousAxisLabelsTypeFormat
  | FirstAxisLabelsTypeFormat;

export interface LabelFormat {
  rotation?: number;
  showLabels?: boolean;
}

export interface HeaderFieldFormat {
  [FieldHeaderTypes.Default]?: TextFormat;
  [FieldHeaderTypes.Title]?: TitleFormat;
  [FieldHeaderTypes.Label]?: LabelFormat;
  [FieldHeaderTypes.ShowValues]?: ValuesFormat;
  [FieldAxisTypes.BinnedAxisLabel]?: BinnedAxisLabelTypeFormat;
}

export interface LegendFieldFormat {
  [FieldLegendTypes.Title]?: string;
  [FieldLegendTypes.Position]?: LegendPosition;
  [FieldLegendTypes.Show]?: boolean;
  [FieldLegendTypes.ShowTitle]?: boolean;
  [FieldLegendTypes.SplitLegend]?: boolean;
}

// TODO: When updated, also update related mock methods in rx-store-service.mock.ts, i.e. createMockFieldFormat
export interface PaneFieldFormat {
  [FieldPaneTypes.Default]?: TextFormat;
  [FieldPaneTypes.Label]?: LabelFormat;
}

export type FormatTypes = TextFormat | TitleFormat;

export interface TitleFormat {
  show: boolean;
  text: string;
  location?: HeaderAxisLocationTypes;
}

export interface ValuesFormat {
  show: boolean;
}

export type TextFormat = TextFormat.TextFormatCompat;

// Disabling this so we can export refining interfaces under
// the well-known type TextFormat, overloading the binding
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace TextFormat {
  export type Union =
    | TextFormat.DefaultType
    | TextFormat.NumberType
    | TextFormat.PercentType
    | TextFormat.FinancialType
    | TextFormat.DateType
    | TextFormat.UrlType
    | TextFormat.DatePartType;

  interface ITextFormat {
    type: FormatType;
    colType?: DataType.Name;
    shortcutType?: FormatShortcutType;
    horizontalAlignment?: HorizontalAlignment;
  }

  /**
   * @description This is a catch-all that supports incrementally patching in fields
   * or hot-swapping the type where refinement invalidation might otherwise yell at you.
   * This is relevant / applicable to the existing usage of TextFormat
   *
   * @example
   * const textFormat: ChartTypes.TextFormat = {
   *  type: ChartTypes.FormatType.Default,
   * }
   *
   * // Won't gripe when assigning different type
   * textFormat.type = ChartTypes.FormatType.Date;
   *
   * @example
   * const textFormat: ChartTypes.TextFormat.DateType = {
   *  type: ChartTypes.FormatType.Date,
   *  dateFormat: '',
   * }
   *
   * // Will gripe when assigning different type
   * // Type 'FormatType.Default' is not assignable to type 'FormatType.Date'
   * textFormat.type = ChartTypes.FormatType.Default;
   *
   */
  export interface TextFormatCompat extends ITextFormat {
    type: FormatType;
    // ---
    // Bag
    // ---
    grouping?: DigitGrouping;
    negativeBehavior?: NegativeBehavior;
    precision?: DecimalPrecision;
    decimalStyle?: DigitSeparator;
    separator?: DigitSeparator;
    currencySymbolPrefix?: string;
    currencySymbolSuffix?: string;
    currencyPreset?: CurrencyPreset;
    linkStyle?: LinkStyle;
    linkColor?: LinkColor;
    linkDisplayText?: string;
    linkURLPattern?: string;
    shouldEncodeURL?: boolean;
    percentTransform?: boolean;
    dateFormat?: string;
    displayUnit?: DisplayUnit;
    textWrappingEnabled?: boolean;
  }

  // Explicit TextFormat types with refinement validation/invalidation

  export interface DefaultType extends TextFormatCompat {
    type: FormatType.Default;
  }

  export interface NumberType extends ITextFormat {
    type: FormatType.Number;
    colType?: DataType.Name.Decimal | DataType.Name.Float | DataType.Name.Integer;
    shortcutType?: FormatShortcutType.Automatic | FormatShortcutType.LongNumber | FormatShortcutType.ShortNumber;
    separator: DigitSeparator;
    grouping: DigitGrouping;
    decimalStyle: DigitSeparator;
    precision: DecimalPrecision;
    displayUnit: DisplayUnit;
  }

  export interface PercentType extends ITextFormat {
    type: FormatType.Percent;
    colType?: DataType.Name.Decimal | DataType.Name.Float | DataType.Name.Integer;
    shortcutType?: FormatShortcutType.Automatic | FormatShortcutType.Percent;
    separator: DigitSeparator;
    grouping: DigitGrouping;
    decimalStyle: DigitSeparator;
    precision: DecimalPrecision;
    percentTransform: boolean;
  }

  export interface FinancialType extends ITextFormat {
    type: FormatType.Financial;
    colType?: DataType.Name.Decimal | DataType.Name.Float | DataType.Name.Integer;
    shortcutType?: FormatShortcutType.Automatic | FormatShortcutType.Currency | FormatShortcutType.Accounting;
    currencyPreset: CurrencyPreset;
    currencySymbolPrefix: string;
    currencySymbolSuffix: string;
    separator: DigitSeparator;
    grouping: DigitGrouping;
    decimalStyle: DigitSeparator;
    precision: DecimalPrecision;
    negativeBehavior: NegativeBehavior;
    displayUnit: DisplayUnit;
  }

  // Alias. From looking through the codes it seems
  // we'd prefer "currency" over "financial" at some point
  export type CurrencyType = FinancialType;

  export interface DateType extends ITextFormat {
    type: FormatType.Date;
    colType?: DataType.Name.DateTime | DataType.Name.Timestamp;
    shortcutType?:
      | FormatShortcutType.Automatic
      | FormatShortcutType.MonthYear
      | FormatShortcutType.MonthDay
      | FormatShortcutType.Year
      | FormatShortcutType.YearMonthDate
      | FormatShortcutType.DateAndTime
      | FormatShortcutType.TimeISO;
    dateFormat: string;
  }

  export interface UrlType extends ITextFormat {
    type: FormatType.Url;
    colType?: DataType.Name.String;
    shortcutType?: FormatShortcutType.Automatic | FormatShortcutType.Url;
    linkStyle: LinkStyle;
    linkColor: LinkColor;
    linkDisplayText?: string;
    linkURLPattern?: string;
    shouldEncodeURL?: boolean;
  }

  export interface DatePartType extends ITextFormat {
    type: FormatType.DatePart;
    shortcutType?:
      | FormatShortcutType.Automatic
      | FormatShortcutType.FullName
      | FormatShortcutType.ShortName
      | FormatShortcutType.Ordinal
      | FormatShortcutType.Numeric;
  }
}
